// If the input is wrapped by elements with v-if, the DOM insertion element will cause it to lose
// focus after the first character is input. This is very bad UX.
<template>
  <div class="search-bar">
    <input ref="searchbar" :key="'main-search'" v-model="q" type="search" :class="{main: true, 'results-shown': showResultList && showResults}" :autosave="autosaveKey" :placeholder="placeholder" @focus="focus" @blur="blur" @keyup="onkey">
    <button v-if="q" key="search-clear" class="clear" @click="clear">
      Clear
    </button>
    <ul v-if="showResultList && showResults" key="search-results" class="results">
      <li v-if="showingRecent" class="section-header">
        Recent
      </li>
      <li v-if="results.length < 1" class="no-results">
        No Results
      </li>
      <component v-else v-for="(result, i) in results" :key="i" 
        :is="result.component"
        v-bind="result" 
        :class="{selected: selectedResult==result}" 
        @resultClick="onResultClick"
      />
    </ul>
    <div v-if="showResultList && showResults" key="search-modal-cover" class="modal-cover" @click="showResults=false">
&nbsp;
    </div>
  </div>
</template>

<style lang="scss">
.search-bar {

  position: relative;
  
  &:before {
    // search magnifying glass
    z-index: 9;
    font-size: 18px;
    font-weight: 900;
    display: block;
    position: absolute;
    z-index: 22;
    left: 12px; 
    top: 50%;
    transform: translateY(-53%);
    font-family: 'Font Awesome 5 Free';
    content: '\f002';
    color: var(--text-color);
    text-shadow: 0 -2px 3px var(--neg-color);
    pointer-events: none;
  }
  
  input.main { 
    -webkit-appearance: none;
    
    position: relative;
    z-index: 8;
    font-size: 22px;
    height: 40px;
    width: 100%;
    min-width: 33px;
    border: none;
    margin: 0 !important;
    padding-left: 3ex;
    border-radius: var(--br);
    outline: none;
    
    &:not(:placeholder-shown) {
      border: 1px solid rgba(0,0,0,0.4) !important;
    }
    
    &::-webkit-search-cancel-button {
      // We're going to show our own clear button.
      display: none;
    }
    
    &.results-shown {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      z-index: 21;
    }
  }
  
  .clear {
    position: absolute;
    right: 18px;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(128,128,128,0.7);
    z-index: 23;
    border: none;
    border-radius: 50%;
    //    left: calc(50vw - 12px - 18px); //? There's gotta be a reason it was left-origin'd.
    color: var(--neg-color);
    text-align: center;
    text-indent: -9999px;
    
    &:before {
      content: '\f00d';
      color: var(--neg-color);
      text-indent: 2px; // ?
      margin: 0;
      background: transparent;
      display: block;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      font-transform: small-caps;
    }
    
    &:hover {
      background: var(--text-color);
      transition: none;
    }
  }
 
  .results {
    position: absolute;
    z-index: 21;
    list-style: none;
    background: var(--popover-abgcolor);
    box-shadow: 0 14px 18px rgba(0,0,0,0.5);
    backdrop-filter: blur(10px);
    margin: 0 0px;
    border-radius: 6px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    
    padding: 1em 0;
    box-sizing: border-box;
    width: 100%;
    
    .no-results {
      text-align: center;
      line-height: 56px;
    }

    .section-header {
      text-transform: uppercase;
      padding: 0 0.75rem 0.4rem;
      color: var(--text-dim1-color);
      font-size: 0.8rem;
      line-height: 1rem;
    }
    
    :not(.section-header) {
      li:hover,
      li:focus {
        background: var(--row-hover-bgcolor) !important;
      }
    }    
    
    li.selected {
      background: var(--row-hover-bgcolor) !important;
      outline: 4px solid var(--accent-color);
      border-radius: var(--br-sharp);
    }
    
  }
  
  .modal-cover {
    z-index: 20;
  }
}


@media screen and (max-width: 437px){
  .results {
    left: 0;
    right: 0;
    width: auto;
  }
}

</style>

<script>
import eventbus from '@/eventbus.js'
import { fuzzyScore } from '@/lib/levenshtein.js'
import { store } from '@/shared-store.js'
import settings from '@/lib/settings.js'
import SrrParticipant from '@/components/search-result-row/srr-participant.vue'
import SrrParticipantName from '@/components/search-result-row/srr-participant-name.vue'
import SrrRaceEvent from '@/components/search-result-row/srr-race-event.vue'

export default {
  components: { SrrParticipant, SrrParticipantName, SrrRaceEvent },
  emits: ['resultClick', 'focus', 'complete', 'query'],
  props: {
    autosaveKey: {
      type: String,
      required: false,
      default: 'momentum-global'
    },
    
    debug: {
      required: false,
      default: () => false
    },

    participants: {
      type: Array,
      default: undefined
    },

    includeRaceEvents: {
      type: Boolean,
      default: true
    },

    showRecent: {
      type: Boolean,
      default: true
    },

    showResultList: {
      type: Boolean,
      default: true
    },

    placeholder: {
      type: String,
      default: ''
    },

    participantComponent: {
      type: [Object, String],
      default: () => SrrParticipant
    }
  },
  
  data: () => ({
    q: '',
    selectedResultIndex: -1, // keyboard focus for results
    showResults: false
  }),
  
  computed: {
    allParticipants() {
      if (this.participants) return this.participants;

      // Prefer the active raceEvent's related participants directly so search
      // does not scan every participant in the store on each keystroke.
      return this.sharedStore.getters.activeRaceEvent?.races?.flatMap(r=>r.participants) || [];
    },
		
		allRaceEvents() {
			const raceEvents = store.state.raceEvent;
			if (!raceEvents) return undefined;
			return Object.values(raceEvents);
		},
    
		selectedResult() {
			return this.results[this.selectedResultIndex];
		},

    showingRecent() {
      return !this.q.length;
    },

    recentResults() {
      if (!this.showRecent) return [];

      const activeRaceEventId = this.sharedStore.getters.activeRaceEvent?.id;
      const participantsById = new Map(
        this.allParticipants.map(participant => [String(participant.id), participant])
      );

      return settings.recentParticipants
        .filter(item => String(item.raceEventId) === String(activeRaceEventId))
        .map(item => participantsById.get(String(item.participantId)))
        .filter(Boolean)
        .map(subject => ({
          component: this.participantComponent,
          subject,
          score: 0,
        }));
    },
		
    results() {
      if (!this.q.length)
        return this.recentResults;
      
      const q = this.q.toLowerCase();
      
      // Push into different arrays. We want to give up filtering the array when max 
      // results are found. Array.some stops when you return false. Our lambdas
      // return the correct response to either keep going or not.
      //
      let participants = new Set();
      let reject = (dest) => false; //(dest.length > 10);
      let accept = (dest, x, component, attrs={}) => {
				let o = Object.assign({
					component,
					subject: x,
				}, attrs);
				dest.add(o);
        return false;
      }
        
      // Iterate participants until we have enough matches
      this.allParticipants?.some(par => {
        if (par.bib == q) 
					return accept(participants, par, this.participantComponent);

				let ld = fuzzyScore(q, `${par.firstName} ${par.lastName}`)
				if (ld < 0.85) 
					return accept(participants, par, this.participantComponent, {score: ld });
        
        if (par.notes && par.notes.includes(q)) 
					return accept(participants, par, this.participantComponent);
        
        return reject(participants);
      });
			
			let raceEvents = new Set();
      if (this.includeRaceEvents) {
        this.allRaceEvents?.some(raceEvent => {
          let ld = fuzzyScore(q, raceEvent.name);

          if (ld < 0.9) 
            accept(raceEvents, raceEvent, SrrRaceEvent, {score: ld });
          else
            reject(raceEvents);
        });
      }

      return [
        ...raceEvents,
        ...participants
      ].sort((a,b)=> a.score - b.score ).slice(0,15);
    }
  },
  
  watch: {
    q(newVal) {
      this.$emit('query', newVal);
      this.$nextTick(() => {
        if (newVal) {
          this.showResults = true;
        }
      });
    },
    
    showResults(newValue) {
      if (false === newValue) {
        this.$emit('complete', this);
      }
    }
  },
  
  mounted() {
    eventbus.on('cmd-f', this.handleCmdF);
    eventbus.on('escapePressed', this.handleEscapePressed);
  },

  unmounted() {
    eventbus.removeListener('cmd-f', this.handleCmdF);
    eventbus.removeListener('escapePressed', this.handleEscapePressed);
  },
  
  methods: {
    handleCmdF(e) {
      e.preventDefault();

      if (!this.$refs.searchbar)
        // not fully loaded yet? Don't want this codepath poisoned.
        return;
      
      this.$refs.searchbar.focus();
      this.$refs.searchbar.select();
    },

    handleEscapePressed() {
      this.showResults = false;
      this.$refs.searchbar?.blur();
    },

    onResultClick(subject) {
      this.showResults=false;
      this.$emit('resultClick', subject);
    },
  
    focus() {
      this.$emit('focus', this);

      this.showResults = true;
      if (this.q.length) {
        this.$refs.searchbar.select();
      }
    },

    blur() {
      console.debug('blur');
      if (!this.showResults) {
        this.$emit('complete', this);
      }
    },
    
    clear() {
      this.q = '';
      this.selectedResultIndex = -1;
      this.showResults = true;
      this.$refs.searchbar.focus();
    },
    
    onkey(e) {
      let i;
			console.log(e.key);
      switch (e.key) {
        default: 
          // not interesting
					this.selectedResultIndex = -1;
          return;

        case 'Escape':
          this.showResults = false;
          this.$refs.searchbar.blur();
          break;
                   
        case 'ArrowDown':
          e.preventDefault();
					if (this.results.length < 1) {
						this.selectedResultIndex = -1;
						break;
					}
						
          this.selectedResultIndex = (this.selectedResultIndex + 1) % this.results.length;
					console.debug(`i: ${this.selectedResultIndex}`);
          break;

        case 'ArrowUp':
          e.preventDefault();
					let i = this.selectedResultIndex - 1;
					if (isNaN(i)) {
						this.selectedResultIndex = -1;
						break;
					}
					this.selectedResultIndex = i < 0 ? 
						this.results.length - 1 :
						i;
					console.debug(`i: ${this.selectedResultIndex}`);
          break;

        case 'Enter': {
          e.preventDefault();
          this.onResultClick(this.selectedResult?.subject);
          break;
        }
      }
    }
  }
}

</script>
